import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Upload, FileSpreadsheet, Loader2, Check, X, Save } from "lucide-react";
import { apiClient } from "@/lib/api";
import { useCollections } from "@/context/CollectionContext";
import { useTier } from "@/context/AuthContext";
import Locked from "@/components/Locked";

// CDI Master Rule — Invoice parser is Premium-only. Trial + Basic must
// never see the form. This wrapper short-circuits before the inner
// component's hooks run, so Rules-of-Hooks are respected. Backend also
// 403s on /api/invoice-ocr as defence in depth.
export default function InvoiceImport() {
  const { canInvoiceOCR } = useTier();
  if (!canInvoiceOCR) {
    return (
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-8" data-testid="invoice-import-page-locked">
        <Locked
          testid="invoice-import-locked"
          title="Invoice import is not available"
          message="This feature is not part of your current plan."
        />
      </div>
    );
  }
  return <InvoiceImportInner />;
}

function InvoiceImportInner() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { collections, activeId } = useCollections();
  const fileRef = useRef(null);

  const [collectionId, setCollectionId] = useState(searchParams.get("collection_id") || activeId || "");
  const [categoryId, setCategoryId] = useState("");
  const [categories, setCategories] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [parsed, setParsed] = useState(null);
  const [rows, setRows] = useState([]); // {description, qty, line_total, selected, edited_name}
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [catRes, colRes] = await Promise.all([
          apiClient.get("/categories"),
          apiClient.get("/collections"),
        ]);
        setCategories(catRes.data || []);
        // Default collection if user hasn't already set one via URL/context.
        const def = (colRes.data || []).find((c) => c.is_default);
        if (def) setCollectionId((cur) => cur || def.id);
        // Sticky last-used category, only if it lives in the current collection.
        try {
          const last = localStorage.getItem("cdi.lastCategoryId") || "";
          if (last) {
            const found = (catRes.data || []).find((c) => c.id === last);
            if (found) {
              setCategoryId((cur) => cur || last);
              setCollectionId((cur) => cur || found.collection_id || "");
            }
          }
        } catch (_e) { /* noop */ }
      } catch { /* */ }
    })();
  }, []);

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 12 * 1024 * 1024) { toast.error("File > 12 MB"); return; }
    const r = new FileReader();
    r.onload = async () => {
      setUploading(true);
      try {
        const { data } = await apiClient.post("/invoice-ocr", { file_data: r.result, filename: file.name });
        setParsed(data);
        const items = (data.line_items || []).map((li) => ({
          ...li,
          selected: true,
          edited_name: li.description,
        }));
        setRows(items);
        if (items.length === 0) {
          toast.message("No line items detected. You can still see the invoice metadata.");
        } else {
          toast.success(`Found ${items.length} line item${items.length === 1 ? "" : "s"}.`);
        }
      } catch (er) {
        toast.error(er?.response?.data?.detail || "OCR failed.");
      } finally {
        setUploading(false);
      }
    };
    r.readAsDataURL(file);
  };

  const toggle = (idx) => setRows((p) => p.map((r, i) => i === idx ? { ...r, selected: !r.selected } : r));
  const editName = (idx, val) => setRows((p) => p.map((r, i) => i === idx ? { ...r, edited_name: val } : r));
  const editPrice = (idx, val) => setRows((p) => p.map((r, i) => i === idx ? { ...r, line_total: Number(val) || 0 } : r));

  const saveAll = async () => {
    if (!collectionId) { toast.error("Pick a collection."); return; }
    if (!categoryId) { toast.error("Pick a category."); return; }
    const selected = rows.filter((r) => r.selected);
    if (selected.length === 0) { toast.error("Pick at least one line item."); return; }
    const blanks = selected.filter((r) => !(r.edited_name || r.description || "").trim());
    if (blanks.length > 0) { toast.error("Every selected row needs a description."); return; }
    setSaving(true);
    try {
      const invoiceNumber = parsed?.invoice_number || "";
      const payloads = selected.map((r) => {
        const name = (r.edited_name || r.description || "").trim();
        return {
          name,
          item_name: name,
          category_id: categoryId,
          collection_id: collectionId,
          purchase_price: Number(r.line_total) || 0,
          estimated_value: 0,
          currency_code: "NZD",
          invoice_number: invoiceNumber,
          item_data: { "Item Name": name, Quantity: r.qty || 1 },
          image: "",
        };
      });
      const { data } = await apiClient.post("/items/bulk", payloads);
      toast.success(`Created ${data.length} item${data.length === 1 ? "" : "s"}.`);
      navigate(collectionId ? `/collections/${collectionId}` : "/items");
    } catch (e) {
      const raw = e?.response?.data?.detail;
      const msg = Array.isArray(raw) ? raw.map((x) => x?.msg).join("; ") : typeof raw === "string" ? raw : "Save failed.";
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8 sm:py-10" data-testid="invoice-import">
      <nav aria-label="Breadcrumb" className="flex items-center gap-1.5 flex-wrap text-xs mb-4" data-testid="ii-breadcrumb">
        <Link to="/" className="text-[10px] uppercase tracking-wider font-medium vp-muted px-2 py-0.5 rounded-full border border-[hsl(var(--border))] hover:bg-[hsl(var(--muted))] hover:text-[hsl(var(--gold-strong))] dark:hover:text-[hsl(var(--gold))] transition-colors duration-fast">Home</Link>
        <span className="vp-muted">→</span>
        <Link to="/items" className="text-[10px] uppercase tracking-wider font-medium vp-muted px-2 py-0.5 rounded-full border border-[hsl(var(--border))] hover:bg-[hsl(var(--muted))] hover:text-[hsl(var(--gold-strong))] dark:hover:text-[hsl(var(--gold))] transition-colors duration-fast">Items</Link>
        <span className="vp-muted">/</span>
        <span className="text-xs font-medium">Import Invoice</span>
      </nav>

      <div className="vp-label mb-2">Invoice Import</div>
      <h1 className="text-3xl sm:text-4xl font-bold tracking-tight mb-2">Multi-Item Invoice Parser</h1>
      <p className="text-sm vp-muted mb-8 max-w-xl">Upload a PDF or image of an invoice. We&apos;ll detect line items so you can create multiple items at once.</p>

      {/* Where these items go — visible from first paint so the default
          collection prefill + sticky category restore are obvious to the user. */}
      <section className="vp-card p-6 mb-6" data-testid="ii-settings">
        <h2 className="text-sm font-semibold mb-1">Where should these items go?</h2>
        <p className="text-xs vp-muted mb-4">Prefilled from your default collection and last-used category. Change before uploading if needed.</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-medium mb-1.5 vp-muted">Collection</label>
            <select data-testid="ii-collection" value={collectionId} onChange={(e) => setCollectionId(e.target.value)} className="vp-select w-full">
              <option value="">— None —</option>
              {collections.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium mb-1.5 vp-muted">Category</label>
            <select data-testid="ii-category" value={categoryId} onChange={(e) => setCategoryId(e.target.value)} className="vp-select w-full">
              <option value="">— None —</option>
              {categories
                .filter((c) => !collectionId || c.collection_id === collectionId)
                .map((c) => <option key={c.id} value={c.id}>{c.category_name}</option>)}
            </select>
          </div>
        </div>
      </section>

      {/* Upload */}
      <section className="vp-card p-6 mb-6" data-testid="ii-upload-section">
        <div className="flex items-center gap-3 flex-wrap">
          <button onClick={() => fileRef.current?.click()} data-testid="ii-upload-btn" disabled={uploading} className="vp-btn vp-btn-primary disabled:opacity-50">
            {uploading ? <Loader2 size={16} className="animate-spin" /> : <Upload size={16} />}
            {uploading ? "Reading…" : (parsed ? "Re-upload" : "Upload Invoice (PDF or Image)")}
          </button>
          <input ref={fileRef} type="file" accept="image/*,application/pdf" onChange={onFile} className="hidden" data-testid="ii-upload-input" />
          {parsed?.filename && <span className="text-sm vp-muted">{parsed.filename}</span>}
        </div>
      </section>

      {/* Detected line items */}
      {parsed && (
        <section className="vp-card p-6 mb-6" data-testid="ii-line-items">
          <h2 className="text-sm font-semibold mb-4 flex items-center gap-2">
            <FileSpreadsheet size={14} /> Detected Line Items ({rows.length})
          </h2>
          {rows.length === 0 ? (
            <p className="text-sm vp-muted text-center py-8">No line items detected automatically. You can still add items manually from <Link to="/items/new" className="underline">Item Entry</Link>.</p>
          ) : (
            <div className="space-y-2">
              <div className="hidden sm:grid grid-cols-12 gap-3 px-3 py-2 vp-label border-b border-[hsl(var(--border))]">
                <div className="col-span-1">Use</div>
                <div className="col-span-6">Description</div>
                <div className="col-span-2 text-right">Qty</div>
                <div className="col-span-3 text-right">Price</div>
              </div>
              {rows.map((r, idx) => (
                <div key={idx} className="grid grid-cols-12 gap-3 items-center px-3 py-2 border-b border-[hsl(var(--border))] last:border-b-0" data-testid={`ii-line-${idx}`}>
                  <div className="col-span-1">
                    <input type="checkbox" checked={r.selected} onChange={() => toggle(idx)} data-testid={`ii-line-${idx}-select`} className="rounded accent-[hsl(var(--gold))]" />
                  </div>
                  <div className="col-span-11 sm:col-span-6">
                    <input
                      value={r.edited_name}
                      onChange={(e) => editName(idx, e.target.value)}
                      data-testid={`ii-line-${idx}-name`}
                      className="vp-input !py-1.5 !text-sm"
                    />
                  </div>
                  <div className="col-span-3 sm:col-span-2 text-right text-sm vp-muted">×{r.qty}</div>
                  <div className="col-span-9 sm:col-span-3">
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={r.line_total}
                      onChange={(e) => editPrice(idx, e.target.value)}
                      data-testid={`ii-line-${idx}-price`}
                      className="vp-input !py-1.5 !text-sm text-right font-mono"
                    />
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>
      )}

      {/* Invoice metadata */}
      {parsed && (
        <section className="vp-card p-6 mb-6" data-testid="ii-meta">
          <h2 className="text-sm font-semibold mb-4">Detected Invoice Details</h2>
          <dl className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
            <Meta label="Invoice #" value={parsed.invoice_number || "—"} />
            <Meta label="Date"      value={parsed.purchase_date || "—"} />
            <Meta label="Seller"    value={parsed.purchased_from || "—"} />
            <Meta label="Total"     value={parsed.cost_price_inc_gst ? `$${Number(parsed.cost_price_inc_gst).toFixed(2)}` : "—"} accent />
          </dl>
        </section>
      )}

      {/* Save */}
      {parsed && rows.length > 0 && (
        <div className="flex justify-end gap-2">
          <Link to="/items" className="vp-btn vp-btn-ghost vp-btn-sm border border-[hsl(var(--border))]">Cancel</Link>
          <button onClick={saveAll} disabled={saving} data-testid="ii-save-all" className="vp-btn vp-btn-primary disabled:opacity-50">
            {saving ? <Loader2 size={15} className="animate-spin" /> : <Save size={15} />}
            {saving ? "Saving…" : `Create ${rows.filter((r) => r.selected).length} Item${rows.filter((r) => r.selected).length === 1 ? "" : "s"}`}
          </button>
        </div>
      )}
    </div>
  );
}

const Meta = ({ label, value, accent }) => (
  <div>
    <dt className="vp-label mb-1">{label}</dt>
    <dd className={`font-mono text-sm ${accent ? "text-[hsl(var(--gold-strong))] dark:text-[hsl(var(--gold))] font-semibold" : ""}`}>{value}</dd>
  </div>
);
